
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";
import { VideoUpload } from "@/components/VideoUpload";

const facilitySchema = z.object({
  name: z.string().min(1, "Facility name is required"),
  description: z.string().optional(),
  facility_type: z.string().min(1, "Facility type is required"),
  image_url: z.string().optional(),
  video_url: z.string().optional(),
});

type FacilityFormData = z.infer<typeof facilitySchema>;

interface AddFacilityDialogProps {
  providerId: string;
  onFacilityAdded: () => void;
}

export const AddFacilityDialog = ({ providerId, onFacilityAdded }: AddFacilityDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<FacilityFormData>({
    resolver: zodResolver(facilitySchema),
    defaultValues: {
      name: "",
      description: "",
      facility_type: "",
      image_url: "",
      video_url: "",
    },
  });

  const onSubmit = async (data: FacilityFormData) => {
    setIsLoading(true);
    console.log("Creating facility with data:", data);

    try {
      const facilityData = {
        provider_id: providerId,
        name: data.name,
        description: data.description || null,
        facility_type: data.facility_type,
        image_url: data.image_url || null,
        video_url: data.video_url || null,
      };

      console.log("Facility data to insert:", facilityData);

      const { data: newFacility, error } = await supabase
        .from('facilities')
        .insert(facilityData)
        .select()
        .single();

      if (error) {
        console.error("Error creating facility:", error);
        throw error;
      }

      console.log("Facility created successfully:", newFacility);

      toast({
        title: "Success",
        description: "Facility added successfully!",
      });

      // Reset form and close dialog
      form.reset();
      setOpen(false);
      onFacilityAdded();

    } catch (error: any) {
      console.error("Error creating facility:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add facility",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUploaded = (url: string) => {
    form.setValue("image_url", url);
  };

  const handleVideoUploaded = (url: string) => {
    form.setValue("video_url", url);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Facility
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Facility</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Infrared Sauna" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="facility_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Type *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select facility type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Sauna">Sauna</SelectItem>
                      <SelectItem value="Ice Bath">Ice Bath</SelectItem>
                      <SelectItem value="Cryotherapy">Cryotherapy</SelectItem>
                      <SelectItem value="Fitness Equipment">Fitness Equipment</SelectItem>
                      <SelectItem value="Recovery Room">Recovery Room</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe your facility..." 
                      {...field} 
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Image</FormLabel>
                  <FormControl>
                    <ImageUpload
                      onImageUploaded={handleImageUploaded}
                      currentImageUrl={field.value}
                      bucketName="facility-images"
                      folder="facilities"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="video_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Video</FormLabel>
                  <FormControl>
                    <VideoUpload
                      onVideoUploaded={handleVideoUploaded}
                      currentVideoUrl={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Adding..." : "Add Facility"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
